import React, { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useApiClient } from "../../../data/clients/ApiClient";
import {
  PriceImportJobDto,
  PriceImportJobStatus,
  PriceImportStatusResponseDto,
} from "../../../data/models/responses/PriceImportDto";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { useSuccessToast, useErrorToast } from "../../components/Toast/Toast";
import LoadingElement from "../../components/loading-element/loading-element";
import { LinearProgress, Collapse } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faCheck,
  faSpinner,
  faClock,
  faExclamationTriangle,
  faChevronDown,
  faChevronUp,
  faDatabase,
  faServer,
  faRotate,
  faCircleCheck,
  faCircleXmark,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useConfirmationDialog } from "../../components/Dialogs/confirmation-dialog";

// Status labels for user-friendly display
const STATUS_LABELS: Record<PriceImportJobStatus, string> = {
  StoppingService: "Stopper logo check service",
  Importing: "Importerer priser",
  StartingService: "Genstarter logo check service",
  Completed: "Fuldført",
  Failed: "Fejlet/Annulleret",
};

// Status colors
const STATUS_COLORS: Record<PriceImportJobStatus, string> = {
  StoppingService: "text-amber-600",
  Importing: "text-blue-600",
  StartingService: "text-amber-600",
  Completed: "text-green-600",
  Failed: "text-slate-600",
};

const STATUS_BG_COLORS: Record<PriceImportJobStatus, string> = {
  StoppingService: "bg-amber-50 border-amber-200",
  Importing: "bg-blue-50 border-blue-200",
  StartingService: "bg-amber-50 border-amber-200",
  Completed: "bg-green-50 border-green-200",
  Failed: "bg-yellow-50 border-yellow-200",
};

// Phase steps for the progress indicator
type Phase = "StoppingService" | "Importing" | "StartingService" | "Completed";
const PHASES: Phase[] = [
  "StoppingService",
  "Importing",
  "StartingService",
  "Completed",
];

function getPhaseIndex(status: PriceImportJobStatus): number {
  if (status === "Failed") return -1;
  return PHASES.indexOf(status as Phase);
}

export function PriceImportScreen() {
  const { accounts } = useMsal();
  const apiClient = useApiClient();
  const userEmail = accounts[0]?.username || "admin";

  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [statusData, setStatusData] =
    useState<PriceImportStatusResponseDto | null>(null);
  const [historyJobs, setHistoryJobs] = useState<PriceImportJobDto[]>([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use ref to avoid recreating functions on every render
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  // Fetch functions that use the ref
  const fetchStatus = async () => {
    try {
      const response = await apiClientRef.current.getYearlyPriceImportStatus();
      setStatusData(response);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching price import status:", err);
      setError(err?.message || "Failed to fetch status");
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await apiClientRef.current.getYearlyPriceImportHistory();
      setHistoryJobs(response.jobs || []);
    } catch (err: any) {
      console.error("Error fetching price import history:", err);
    }
  };

  const refreshData = async () => {
    await Promise.all([fetchStatus(), fetchHistory()]);
  };

  // Initial load - only runs once on mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const [statusResponse, historyResponse] = await Promise.all([
          apiClientRef.current.getYearlyPriceImportStatus(),
          apiClientRef.current.getYearlyPriceImportHistory(),
        ]);

        if (isMounted) {
          setStatusData(statusResponse);
          setHistoryJobs(historyResponse.jobs || []);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message || "Failed to load data");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run on mount

  // Polling logic - depends only on hasActiveJob status
  useEffect(() => {
    if (isLoading || !statusData) return;

    const hasActiveJob = statusData.hasActiveJob;
    const pollInterval = hasActiveJob ? 30000 : 300000; // 30 seconds if active, 5 minutes otherwise

    const intervalId = setInterval(async () => {
      try {
        const response =
          await apiClientRef.current.getYearlyPriceImportStatus();
        setStatusData(response);

        if (hasActiveJob) {
          const historyResponse =
            await apiClientRef.current.getYearlyPriceImportHistory();
          setHistoryJobs(historyResponse.jobs || []);
        }
      } catch (err) {
        console.error("Error during polling:", err);
      }
    }, pollInterval);

    return () => clearInterval(intervalId);
  }, [isLoading, statusData?.hasActiveJob]); // Only re-run when loading state or hasActiveJob changes

  // Start import handler
  const handleStartImport = async () => {
    setIsStarting(true);
    try {
      const response = await apiClientRef.current.startYearlyPriceImport({
        startedBy: userEmail,
      });

      if (response.success) {
        useSuccessToast(response.message || "Prisimport startet succesfuldt");
        await refreshData();
      } else {
        useErrorToast(response.message || "Kunne ikke starte prisimport");
      }
    } catch (err: any) {
      const message = err?.message || "Kunne ikke starte prisimport";
      useErrorToast(message);
    } finally {
      setIsStarting(false);
    }
  };

  // Cancel import handler
  const handleCancelImport = async () => {
    setIsCancelling(true);
    try {
      const response = await apiClientRef.current.cancelYearlyPriceImport();

      if (response.success) {
        useSuccessToast(response.message || "Prisimport annulleret");
        await refreshData();
      } else {
        useErrorToast(response.message || "Kunne ikke annullere prisimport");
      }
    } catch (err: any) {
      const message = err?.message || "Kunne ikke annullere prisimport";
      useErrorToast(message);
    } finally {
      setIsCancelling(false);
    }
  };

  // Confirmation dialog for starting
  const confirmationDialog = useConfirmationDialog(
    "Er du sikker på, at du vil starte den årlige prisimport?\n\nDenne proces vil midlertidigt deaktivere den eksterne logotjeneste og kan tage 1-3 dage at fuldføre.",
    handleStartImport,
    () => {}
  );

  // Confirmation dialog for cancelling
  const cancelConfirmationDialog = useConfirmationDialog(
    "Er du sikker på, at du vil annullere den igangværende prisimport?\n\nDette vil genstarte logo check servicen.",
    handleCancelImport,
    () => {}
  );

  if (isLoading) {
    return (
      <div className="p-8">
        <JetSportBox>
          <div className="h-96 relative">
            <LoadingElement />
          </div>
        </JetSportBox>
      </div>
    );
  }

  const activeJob = statusData?.hasActiveJob ? statusData.job : null;
  const lastJob = !statusData?.hasActiveJob ? statusData?.lastJob : null;

  return (
    <div className="p-8 space-y-6">
      {confirmationDialog.component}
      {cancelConfirmationDialog.component}

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">
          Årlig prisimport
        </h1>
        <p className="text-sm text-gray-600">
          Administrer den årlige prisimportproces fra Navision. Denne proces
          opdaterer alle logopriser og deaktiverer midlertidigt den eksterne
          logotjeneste.
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-red-500"
          />
          <span className="text-red-700">{error}</span>
          <SecondaryButton className="ml-auto" width="sm" onClick={refreshData}>
            Prøv igen
          </SecondaryButton>
        </div>
      )}

      {/* Current Status Section */}
      <JetSportBox>
        <BoxHeader2>
          <BoxHeaderTypography>Aktuel status</BoxHeaderTypography>
        </BoxHeader2>
        <BoxContent className="!min-h-0 py-6">
          {activeJob ? (
            <ActiveJobView job={activeJob} />
          ) : (
            <NoActiveJobView
              lastJob={lastJob}
              onStartImport={confirmationDialog.open}
              isStarting={isStarting}
            />
          )}
        </BoxContent>
      </JetSportBox>

      {/* Progress Phases Section - Only show when job is active */}
      {activeJob && (
        <JetSportBox>
          <BoxHeader2>
            <BoxHeaderTypography>Importfremskridt</BoxHeaderTypography>
          </BoxHeader2>
          <BoxContent className="!min-h-0 py-6">
            <PhaseIndicator status={activeJob.status} />
          </BoxContent>
        </JetSportBox>
      )}

      {/* Job History Section */}
      <JetSportBox>
        <BoxHeader2
          className="cursor-pointer"
          onClick={() => setHistoryExpanded(!historyExpanded)}
        >
          <div className="flex items-center justify-between w-full">
            <BoxHeaderTypography>Jobhistorik</BoxHeaderTypography>
            <FontAwesomeIcon
              icon={historyExpanded ? faChevronUp : faChevronDown}
              className="text-gray-500"
            />
          </div>
        </BoxHeader2>
        <Collapse in={historyExpanded}>
          <BoxContent className="!min-h-0 py-6 px-0">
            <JobHistoryTable
              jobs={historyJobs}
              onCancelJob={cancelConfirmationDialog.open}
              isCancelling={isCancelling}
            />
          </BoxContent>
        </Collapse>
      </JetSportBox>
    </div>
  );
}

// Active Job View Component
function ActiveJobView({ job }: { job: PriceImportJobDto }) {
  // The API returns remaining count in logosProcessed, so we calculate the actual processed count
  const logosRemaining = job.logosProcessed;
  const processedCount = job.totalLogosToProcess - logosRemaining;

  // Calculate progress percent based on processed count
  const progressPercent =
    job.totalLogosToProcess > 0
      ? (processedCount / job.totalLogosToProcess) * 100
      : 0;

  return (
    <div className="space-y-6">
      {/* Status Badge */}
      <div className="flex items-center gap-4">
        <div
          className={`px-4 py-2 rounded-full border ${
            STATUS_BG_COLORS[job.status]
          } flex items-center gap-2`}
        >
          <FontAwesomeIcon
            icon={faSpinner}
            className={`${STATUS_COLORS[job.status]} animate-spin`}
          />
          <span className={`font-medium ${STATUS_COLORS[job.status]}`}>
            {STATUS_LABELS[job.status]}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Fremskridt</span>
          <span>{progressPercent.toFixed(1)}%</span>
        </div>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: "#e5e7eb",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#3b82f6",
              borderRadius: 5,
            },
          }}
        />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={faDatabase}
          label="Logoer behandlet"
          value={`${job.logosProcessed.toLocaleString()} / ${job.totalLogosToProcess.toLocaleString()}`}
        />
        <StatCard
          icon={faClock}
          label="Startet"
          value={formatDateTime(job.startedAtUtc)}
        />
        <StatCard
          icon={faClock}
          label="Forventet afslutning"
          value={
            job.estimatedCompletionUtc
              ? formatDateTime(job.estimatedCompletionUtc)
              : "Beregner..."
          }
        />
        <StatCard
          icon={faServer}
          label="Startet af"
          value={job.startedBy || "Ukendt"}
        />
      </div>
    </div>
  );
}

// No Active Job View Component
function NoActiveJobView({
  lastJob,
  onStartImport,
  isStarting,
}: {
  lastJob: PriceImportJobDto | null | undefined;
  onStartImport: () => void;
  isStarting: boolean;
}) {
  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-amber-500 mt-0.5"
          />
          <div>
            <h4 className="font-medium text-amber-800 mb-1">VIGTIGT</h4>
            <p className="text-sm text-amber-700">
              Hvis du starter denne proces, vil logo check service midlertidigt
              blive deaktiveret. Importprocessen tager typisk{" "}
              <strong>1-3 dage</strong> at fuldføre. Sørg for, at dette er det
              rette tidspunkt at køre den årlige import.
            </p>
            <p className="text-sm text-amber-700 underline mt-2">
              <strong>
                Start først denne proces, når alle priser er opdateret i
                Navision.
              </strong>
            </p>
          </div>
        </div>
      </div>

      {/* Start Button */}
      <div className="flex items-center gap-4">
        <PrimaryButton
          onClick={onStartImport}
          disabled={isStarting}
          className="!py-3 !px-6"
          width="md"
        >
          <FontAwesomeIcon
            icon={isStarting ? faSpinner : faPlay}
            className={`mr-2 ${isStarting ? "animate-spin" : ""}`}
          />
          {isStarting ? "Starter..." : "Start årlig prisimport"}
        </PrimaryButton>
      </div>

      {/* Last Job Summary */}
      {lastJob && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-4">
            Sidste importjob
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={
                lastJob.status === "Completed" ? faCircleCheck : faCircleXmark
              }
              label="Status"
              value={STATUS_LABELS[lastJob.status]}
              valueColor={STATUS_COLORS[lastJob.status]}
            />
            <StatCard
              icon={faClock}
              label="Startet"
              value={formatDateTime(lastJob.startedAtUtc)}
            />
            <StatCard
              icon={faClock}
              label="Varighed"
              value={
                lastJob.duration ? formatDuration(lastJob.duration) : "N/A"
              }
            />
            <StatCard
              icon={faDatabase}
              label="Logoer behandlet"
              value={`${lastJob.logosProcessed.toLocaleString()}`}
            />
          </div>
          {lastJob.status === "Failed" && lastJob.errorMessage && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-700">{lastJob.errorMessage}</p>
            </div>
          )}
        </div>
      )}

      {/* No previous jobs */}
      {!lastJob && (
        <div className="text-center py-8 text-gray-500">
          <FontAwesomeIcon
            icon={faDatabase}
            className="text-4xl mb-3 text-gray-300"
          />
          <p>Ingen tidligere importjobs fundet.</p>
        </div>
      )}
    </div>
  );
}

// Phase Indicator Component
function PhaseIndicator({ status }: { status: PriceImportJobStatus }) {
  const currentIndex = getPhaseIndex(status);
  const isFailed = status === "Failed";

  const phaseLabels: Record<Phase, string> = {
    StoppingService: "Stop ekstern tjeneste",
    Importing: "Importer priser",
    StartingService: "Genstart tjeneste",
    Completed: "Fuldført",
  };

  const phaseIcons: Record<Phase, any> = {
    StoppingService: faServer,
    Importing: faDatabase,
    StartingService: faRotate,
    Completed: faCheck,
  };

  return (
    <div className="flex items-center justify-between">
      {PHASES.map((phase, index) => {
        const isCompleted = currentIndex > index;
        const isCurrent = currentIndex === index && !isFailed;
        const isPending = currentIndex < index;

        return (
          <React.Fragment key={phase}>
            {/* Phase Circle */}
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all
                  ${
                    isCompleted
                      ? "bg-green-100 border-green-500 text-green-600"
                      : ""
                  }
                  ${
                    isCurrent
                      ? "bg-blue-100 border-blue-500 text-blue-600 animate-pulse"
                      : ""
                  }
                  ${
                    isPending ? "bg-gray-100 border-gray-300 text-gray-400" : ""
                  }
                  ${
                    isFailed && index === 0
                      ? "bg-red-100 border-red-500 text-red-600"
                      : ""
                  }
                `}
              >
                <FontAwesomeIcon
                  icon={isCompleted ? faCheck : phaseIcons[phase]}
                  className={isCurrent ? "animate-spin" : ""}
                />
              </div>
              <span
                className={`
                  mt-2 text-xs font-medium text-center max-w-[80px]
                  ${isCompleted ? "text-green-600" : ""}
                  ${isCurrent ? "text-blue-600" : ""}
                  ${isPending ? "text-gray-400" : ""}
                `}
              >
                {phaseLabels[phase]}
              </span>
            </div>

            {/* Connector Line */}
            {index < PHASES.length - 1 && (
              <div
                className={`
                  flex-1 h-0.5 mx-2
                  ${isCompleted ? "bg-green-500" : "bg-gray-200"}
                `}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Stat Card Component
function StatCard({
  icon,
  label,
  value,
  valueColor = "text-gray-800",
}: {
  icon: any;
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
      <div className="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={icon} className="text-gray-400 text-sm" />
        <span className="text-xs text-gray-500 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className={`text-sm font-medium ${valueColor} truncate`} title={value}>
        {value}
      </p>
    </div>
  );
}

// Job History Table Component
function JobHistoryTable({
  jobs,
  onCancelJob,
  isCancelling,
}: {
  jobs: PriceImportJobDto[];
  onCancelJob: () => void;
  isCancelling: boolean;
}) {
  if (jobs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FontAwesomeIcon
          icon={faDatabase}
          className="text-4xl mb-3 text-gray-300"
        />
        <p>Ingen importhistorik fundet.</p>
      </div>
    );
  }

  // Check if job is active (not completed or failed)
  const isJobActive = (status: PriceImportJobStatus) =>
    status !== "Completed" && status !== "Failed";

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-y border-gray-100">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Startdato
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Varighed
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Logoer behandlet
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Startet af
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Fejl
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Handling
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {jobs.map((job) => (
            <tr key={job.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {formatDateTime(job.startedAtUtc)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <StatusBadge status={job.status} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {job.duration ? formatDuration(job.duration) : "—"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {job.logosProcessed.toLocaleString()} /{" "}
                {job.totalLogosToProcess.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {job.startedBy || "Ukendt"}
              </td>
              <td
                className="px-6 py-4 text-sm text-yellow-600 max-w-xs truncate"
                title={job.errorMessage || ""}
              >
                {job.errorMessage || "—"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                {isJobActive(job.status) ? (
                  <button
                    onClick={onCancelJob}
                    disabled={isCancelling}
                    className="text-slate-500 hover:text-slate-700 disabled:text-gray-300 disabled:cursor-not-allowed p-2 rounded hover:bg-red-50 transition-colors"
                    title="Annuller import"
                  >
                    <FontAwesomeIcon
                      icon={isCancelling ? faSpinner : faTrash}
                      className={isCancelling ? "animate-spin" : ""}
                    />
                  </button>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }: { status: PriceImportJobStatus }) {
  const bgColors: Record<PriceImportJobStatus, string> = {
    StoppingService: "bg-amber-100 text-amber-800",
    Importing: "bg-blue-100 text-blue-800",
    StartingService: "bg-amber-100 text-amber-800",
    Completed: "bg-green-100 text-green-800",
    Failed: "bg-yellow-100 text-yellow-800",
  };

  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-medium ${bgColors[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// Utility: Format date/time to local timezone (Danish)
function formatDateTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString("da-DK", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
}

// Utility: Format duration string
// Handles multiple formats:
// - .NET TimeSpan: "d.hh:mm:ss" or "hh:mm:ss"
// - Total seconds as number string: "4438948"
// - ISO 8601 duration: Not currently supported
function formatDuration(duration: string): string {
  try {
    let totalSeconds = 0;

    // Check if it's a TimeSpan format with colons (e.g., "4.08:30:00" or "08:30:00")
    if (duration.includes(":")) {
      const parts = duration.split(".");
      let days = 0;
      let timePart = duration;

      // Check if there's a day component (e.g., "4.08:30:00")
      if (parts.length >= 2 && !parts[0].includes(":")) {
        days = parseInt(parts[0], 10) || 0;
        timePart = parts[1];
      }

      const timeParts = timePart.split(":");
      const hours = parseInt(timeParts[0], 10) || 0;
      const minutes = parseInt(timeParts[1], 10) || 0;
      const seconds = parseInt(timeParts[2], 10) || 0;

      totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
    } else {
      // Assume it's total seconds (or ticks - .NET ticks are 100-nanosecond intervals)
      const numValue = parseFloat(duration);

      if (numValue > 1e10) {
        // Likely .NET ticks (10,000,000 ticks per second)
        totalSeconds = Math.floor(numValue / 10000000);
      } else if (numValue > 1e6) {
        // Likely milliseconds
        totalSeconds = Math.floor(numValue / 1000);
      } else {
        // Assume seconds
        totalSeconds = Math.floor(numValue);
      }
    }

    // Convert total seconds to days, hours, minutes
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const result: string[] = [];
    if (days > 0) result.push(`${days} dag${days !== 1 ? "e" : ""}`);
    if (hours > 0) result.push(`${hours} time${hours !== 1 ? "r" : ""}`);
    if (minutes > 0) result.push(`${minutes} min.`);

    return result.length > 0 ? result.join(", ") : "< 1 minut";
  } catch {
    return duration;
  }
}

export default PriceImportScreen;
